/**
 * Top-of-map address search, backed by OpenStreetMap Nominatim.
 *
 * Deliberately *not* using Google Places — see the coding guidelines (rule
 * 10): we keep external deps minimal and avoid keys baked into a public
 * bundle. The host page can replace this component with a Google-backed one
 * later by swapping in a different child of `<BerlinMapCard>`.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale, t } from "../../../shared/i18n";

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  class?: string;
  type?: string;
  osm_type?: string;
  address?: {
    road?: string;
    house_number?: string;
    postcode?: string;
    suburb?: string;
    city_district?: string;
    borough?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    state?: string;
  };
}

/**
 * Decide whether a Nominatim result represents a *city* (or a similarly
 * coarse area) versus a specific address. We want "Berlin" → city bbox,
 * "Sonnenallee 99" → small radius.
 */
function classifyResult(it: NominatimResult): "city" | "address" {
  const a = it.address || {};
  if (a.road || a.house_number) return "address";
  if (
    it.class === "place" &&
    ["city", "town", "village", "municipality"].includes(it.type || "")
  ) {
    return "city";
  }
  if (a.city || a.town || a.village || a.municipality) {
    // Result is at city level but doesn't pin a street.
    const head = it.display_name.split(",")[0]!.trim().toLowerCase();
    const cityName = (
      a.city ||
      a.town ||
      a.village ||
      a.municipality ||
      ""
    ).toLowerCase();
    if (head === cityName) return "city";
  }
  return "address";
}

export interface PickedPlace {
  lat: number;
  lng: number;
  label: string;
  /**
   * What kind of place the user picked. We use this to decide whether to
   * scope the stats to a small radius (a street/POI) or to the whole city
   * bbox (when they searched "Berlin").
   */
  kind: "city" | "address";
}

export interface Props {
  /** Bias geocoding to this city + viewbox. */
  city: string;
  bbox: { west: number; south: number; east: number; north: number } | null;
  onPick: (p: PickedPlace) => void;
}

export function AddressSearch({ city, bbox, onPick }: Props) {
  const locale = useLocale();
  const [value, setValue] = useState("");
  const [items, setItems] = useState<NominatimResult[]>([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrap = useRef<HTMLFormElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  function lookup(q: string) {
    if (!q || q.trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    // We bias by the city's bbox (when known) instead of forcing
    // "<query> <city>" into the query string. That dual approach used to
    // turn a "Berlin" search into "Berlin Berlin" and Nominatim returned
    // the first matching street rather than the city — making it
    // impossible to zoom back out to the whole city via the search bar.
    const params = new URLSearchParams({
      format: "json",
      addressdetails: "1",
      limit: "6",
      countrycodes: "de",
      q: q,
    });
    if (bbox) {
      params.set(
        "viewbox",
        `${bbox.west},${bbox.north},${bbox.east},${bbox.south}`,
      );
      // Don't HARD-bound by bbox — a user typing "Hamburg" in a
      // Berlin-defaulted widget should still get Hamburg back.
    }
    fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { "Accept-Language": locale },
    })
      .then((r) => r.json())
      .then((data: NominatimResult[]) => {
        setItems(Array.isArray(data) ? data : []);
        setOpen(true);
        setActive(-1);
      })
      .catch(() => {
        setItems([]);
        setOpen(true);
      });
  }

  function onInput(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setValue(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => lookup(v), 300);
  }

  function pick(i: number) {
    const it = items[i];
    if (!it) return;
    const lat = Number(it.lat);
    const lng = Number(it.lon);
    const kind = classifyResult(it);
    const label =
      kind === "city"
        ? it.display_name.split(",")[0]!.trim()
        : it.display_name.split(",").slice(0, 2).join(",").trim();
    setValue(label);
    setOpen(false);
    onPick({ lat, lng, label, kind });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(active);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (items.length) pick(active >= 0 ? active : 0);
    else lookup(value);
  }

  const cityLabel = city.charAt(0).toUpperCase() + city.slice(1);

  return (
    <form
      ref={wrap}
      className="cn-map-search"
      onSubmit={onSubmit}
      role="search"
      autoComplete="off"
    >
      <svg
        className="cn-map-search__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        className="cn-map-search__input"
        type="text"
        value={value}
        onChange={onInput}
        onKeyDown={onKeyDown}
        placeholder={t("map.searchPlaceholder", { city: cityLabel }, locale)}
        aria-label={t("map.searchButton", {}, locale)}
      />
      <button
        type="submit"
        className="cn-map-search__btn"
        aria-label={t("map.searchButton", {}, locale)}
      >
        <svg
          className="cn-map-search__btn-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className="cn-map-search__btn-label">
          {t("map.searchButton", {}, locale)}
        </span>
      </button>
      {open && (
        <div className="cn-map-search__suggest">
          {items.length === 0 && (
            <div className="cn-map-search__empty">
              {t("map.searchEmpty", {}, locale)}
            </div>
          )}
          {items.map((it, i) => {
            const a = it.address || {};
            const main =
              [a.road, a.house_number].filter(Boolean).join(" ") ||
              it.display_name.split(",")[0];
            const sub = [
              a.postcode,
              a.suburb || a.city_district || a.borough || a.city,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div
                key={i}
                className={
                  "cn-map-search__opt" + (i === active ? " is-active" : "")
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i);
                }}
              >
                {main}
                <span className="cn-map-search__opt-sub">
                  {sub || cityLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </form>
  );
}
